import React, { useState } from "react";
import {Box, Button, TablePagination, Tooltip, Select, MenuItem, FormControl, InputLabel, ListItemIcon, ListItemText, useTheme} from "@mui/material";
import {GridSelectedRowCount} from "@mui/x-data-grid/components/GridSelectedRowCount";
import {useGridApiContext} from "@mui/x-data-grid";
import { EquipmentRequestStatusEnum } from "graphql/graphQlApiHooks";
import { getLabelStatusRequest } from "../../../../lib/getLabelStatusRequest";
import { getStatusIcon } from "../../../../lib/getStatusIcon";
import { getStatusColor } from "../../../../lib/getStatusColor";
import { getAvailableStatuses } from "../../../../lib/getAvailableStatuses";
import { StatusChip } from "../../../ui/StatusChip";

interface Props {
    selected: any;
    onTakeToWorkHandler: () => void;
    isDisabled: boolean;
    selectedAvailable: number;
    onCreateCombinedEquipmentRequest: () => void
    onAddToCombinedEquipmentRequest: () => void
    combinedRequestsLength: number
    selectedRows: any[]
    onBulkStatusChange: (newStatus: EquipmentRequestStatusEnum) => void
}


export const Footer = (props: Props) => {
    const [selectedStatus, setSelectedStatus] = useState<EquipmentRequestStatusEnum | ''>('');
    const theme = useTheme();

    const selectedStatuses = props.selectedRows.map(row => row.requestStatus);
    const hasRejectedOrSupplied = selectedStatuses.includes(EquipmentRequestStatusEnum.Rejected) || 
                                  selectedStatuses.includes(EquipmentRequestStatusEnum.Supplied);

    const availableStatuses = getAvailableStatuses()
        .filter(status => {
            if (hasRejectedOrSupplied && (status === EquipmentRequestStatusEnum.Rejected || status === EquipmentRequestStatusEnum.Supplied)) {
                return false;
            }
            return true;
        })
        .map(value => ({
            label: getLabelStatusRequest(value),
            value: value
        }));

    const handleStatusChange = (status: EquipmentRequestStatusEnum) => {
        setSelectedStatus(status);
        props.onBulkStatusChange(status);
        setSelectedStatus(''); 
    };

    return (
        <>
            <Box sx={{px: 3, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                <Box display={"flex"} alignItems={"center"} gap={2} flexWrap={"wrap"}>

                    {props.selected.length > 0 ? <>
                        <GridSelectedRowCount selectedRowCount={props.selected.length}/>
                        
                        <Tooltip
                            title={`Принять в работу можно только заявки со статусом 'На рассмотрении', для принятия в работу доступно ${props.selectedAvailable} заявок`}
                        >
                            <Button
                                color={"primary"}
                                variant={"outlined"}
                                size={"small"}
                                sx={{py: .5, px: 2}}
                                onClick={props.onTakeToWorkHandler}
                                disabled={props.isDisabled}
                            >Принять в работу</Button>
                        </Tooltip>

                        <Button
                            color={"primary"}
                            variant={"outlined"}
                            size={"small"}
                            sx={{py: .5, px: 2}}
                            onClick={props.onCreateCombinedEquipmentRequest}
                            disabled={props.selected.length === 1}
                        >Объединить заявки</Button>

                        <Button
                            color={"primary"}
                            variant={"outlined"}
                            size={"small"}
                            sx={{py: .5, px: 2}}
                            onClick={props.onAddToCombinedEquipmentRequest}
                        >Добавить в объединенную</Button>

                        <FormControl 
                            size="small" 
                            sx={{ 
                                minWidth: 260,
                                '& .MuiOutlinedInput-root': {
                                    height: 36,
                                    borderRadius: 1.5,
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.primary.main,
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.primary.main,
                                        borderWidth: 2,
                                    }
                                },
                                '& .MuiInputLabel-root': {
                                    fontSize: '0.875rem',
                                    '&.Mui-focused': {
                                        color: theme.palette.primary.main,
                                    }
                                }
                            }}
                        >
                            <InputLabel>Изменить статус</InputLabel>
                            <Select
                                value={selectedStatus}
                                label="Изменить статус"
                                onChange={(e) => handleStatusChange(e.target.value as EquipmentRequestStatusEnum)}
                                disabled={props.selected.length === 0}
                                MenuProps={{
                                    PaperProps: {
                                        style: { 
                                            maxWidth: 380,
                                            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                                            borderRadius: 12,
                                        },
                                    },
                                    anchorOrigin: {
                                        vertical: 'bottom',
                                        horizontal: 'left',
                                    },
                                    transformOrigin: {
                                        vertical: 'top',
                                        horizontal: 'left',
                                    },
                                }}
                                sx={{
                                    '& .MuiSelect-select': {
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        py: 1,
                                    }
                                }}
                                renderValue={(value) => 
                                    value ? (
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                            {getStatusIcon(value as EquipmentRequestStatusEnum, "1.2rem")}
                                            <StatusChip
                                                status={value as EquipmentRequestStatusEnum}
                                                label={getLabelStatusRequest(value as EquipmentRequestStatusEnum)}
                                                size="small"
                                            />
                                        </Box>
                                    ) : ''
                                }
                            >
                                {availableStatuses.map((statusOption) => (
                                    <MenuItem 
                                        key={statusOption.value} 
                                        value={statusOption.value}
                                        sx={{
                                            py: 1.5,
                                            px: 2,
                                            borderRadius: 1,
                                            mx: 0.5,
                                            my: 0.25,
                                            '&:hover': {
                                                backgroundColor: theme.palette.action.hover,
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: theme.palette.primary.light + '20',
                                                '&:hover': {
                                                    backgroundColor: theme.palette.primary.light + '30',
                                                }
                                            }
                                        }}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                color: getStatusColor(statusOption.value, theme).color,
                                                minWidth: 32,
                                            }}
                                        >
                                            {getStatusIcon(statusOption.value, "1.2rem")}
                                        </ListItemIcon>
                                        <ListItemText
                                            sx={{
                                                '& .MuiTypography-root': {
                                                    color: getStatusColor(statusOption.value, theme).color,
                                                    fontSize: "14px",
                                                    fontWeight: 500,
                                                    whiteSpace: "normal",
                                                }
                                            }}
                                        >
                                            {statusOption.label}
                                        </ListItemText>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                    </> : null}
                </Box>
                {
                    <CustomPagination

                    />
                }
            </Box>
        </>
    );
};

function CustomPagination() {
    const apiRef = useGridApiContext();

    const handleRowsPerPageChange = (event) => {
        apiRef.current.setPageSize(parseInt(event.target.value, 10));
    };

    return (
        <TablePagination
            component="div"
            count={apiRef.current.state.pagination.pageCount * apiRef.current.state.pagination.pageSize}
            page={apiRef.current.state.pagination.page}
            onPageChange={(event, newPage) => apiRef.current.setPage(newPage)}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPage={apiRef.current.state.pagination.pageSize}
            rowsPerPageOptions={[10, 25, 50, 100]}
            SelectProps={{
                MenuProps: {
                    anchorOrigin: {
                        vertical: "top",
                        horizontal: "left",
                    },
                    transformOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                    },
                }
            }}
        />
    );
}